import { Check } from 'lucide-react';
import { useTranslation } from '../../lib/i18n';
import { ISLAND_MARKERS, type OnboardingChrome } from '../../lib/value-first-onboarding';
import { AgentDot } from '../AgentDot';
import styles from './ValueFirstIslandPill.module.css';

const MAX_DOTS = 4;

/**
 * The value-first onboarding's expanded island (`native-apps/shared/onboarding-flow/contract.json` `chrome`): the
 * resting counts, then four markers — `ticked` of them done, the next one outlined — and the step's label. Green
 * while it is the owner's turn, blue while agents work. Not a link: during setup the island reports progress, and
 * the regular island (with its tap to Your agents) returns at `done`. `AgentsIslandBadge` draws it in its slot.
 */
export function ValueFirstIslandPill({ chrome, agentIds, agentCount, skillCount }: { chrome: OnboardingChrome; agentIds: string[]; agentCount: number; skillCount: number }) {
  const { t } = useTranslation();
  const label = t(chrome.labelKey);
  const ring = chrome.tone === 'working' ? 'var(--island-in-progress-bg)' : 'var(--sym-accent-bg)';
  return (
    <span
      role="status"
      aria-label={`${t('island.aria', { agents: agentCount, skills: skillCount })}, ${label}`}
      data-testid="value-first-island"
      data-tone={chrome.tone}
      className={`${styles.pill} ${chrome.tone === 'working' ? styles.working : styles.owner}`}
    >
      <span className={styles.team}>
        {agentIds.length > 0 ? (
          <span className={styles.dots}>
            {agentIds.slice(0, MAX_DOTS).map((id) => (
              <span key={id} className={styles.dot}>
                <AgentDot id={id} size={20} ringColor={ring} />
              </span>
            ))}
          </span>
        ) : (
          <span aria-hidden className={styles.ring} />
        )}
        <span className={styles.count}>
          <span className={styles.number}>{agentCount}</span>
          <span className={styles.unit}>{t(agentCount === 1 ? 'island.agent' : 'island.agents')}</span>
        </span>
        <span aria-hidden className={styles.unit}>
          ·
        </span>
        <span className={styles.count}>
          <span className={styles.number}>{skillCount}</span>
          <span className={styles.unit}>{t(skillCount === 1 ? 'island.skill' : 'island.skills')}</span>
        </span>
      </span>
      <span aria-hidden className={styles.divider} />
      <span aria-hidden className={styles.markers}>
        {Array.from({ length: ISLAND_MARKERS }, (_, i) => {
          const state = i < chrome.ticked ? 'ticked' : i === chrome.ticked ? 'current' : 'next';
          return (
            <span key={i} data-marker={state} className={`${styles.marker} ${styles[state]}`}>
              {state === 'ticked' ? <Check size={8} strokeWidth={4} /> : null}
            </span>
          );
        })}
      </span>
      <span className={styles.label}>{label}</span>
    </span>
  );
}
